import _asyncToGenerator from "@babel/runtime/helpers/esm/asyncToGenerator";
import "regenerator-runtime/runtime.js";
import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.object.to-string.js";
/**
 * @typedef AccessGood
 * @property {string} [modelName]
 * @property {string} [modelId]
 * @property {string} [type]
 * @property {string} [image]
 * @property {string} [link]
 * @property {string} [id]
 * @property {boolean} [soon]
 */
/**
 * @typedef Access
 * @property {string} id
 * @property {string} slug
 * @property {string} link
 * @property {Object.<string, string>} title
 * @property {Object.<string, string>} paymentDescription
 * @property {AccessGood[]} [goods]
 * @property {'simple'|'subscription'} type
 * @property {'month'|'year'} [subscribe_periodicity]
 * @property {string} company
 * @property {string} [thumbnail]
 * @property {boolean} hidden
 * @property {boolean} [isContentSubscription]
 * @property {number} [fullPrice]
 * @property {number} price
 * @property {boolean} useFullPrice
 * @property {string} [saleStart] date string
 * @property {string} [saleEnd] date string
 * @property {{ image: string }} [gain]
 * @property {number} [days]
 * @property {number} [monthes]
 * @property {boolean} [disableReferral]
 * @property {boolean} [multipleBuy]
 * @property {string[]} [certificateOf]
 * @property {string[]} [relatedAccesses]
 * @property {number} [tariffLevel]
 * @property {string[]} [relatedTariffs]
 */
/**
 * @typedef AccessManager
 * @property {string} id
 * @property {string} user
 * @property {Access} accessTariff
 * @property {Access} accessSubscription
 * @property {string} startDate date string
 * @property {string} endDate date string
 * @property {string} [expiredAt] date string
 * @property {'active'|'expired'|'ended'} status
 * @property {number} inactiveDays
 */
/**
 * @typedef AccessManagerStoreState
 * @property {AccessManager[]} managers
 * @property {{ maxInactiveDays: number }} config
 * @property {boolean} hydrated
 */

export var useAccessManagerStore = defineStore({
  id: 'accessManager',
  state: function state() {
    return {
      /** @type {AccessManager[]} */
      managers: [],
      /** @type {{ maxInactiveDays: number }} */
      config: {
        maxInactiveDays: 90
      },
      /** @type {boolean} */
      hydrated: false
    };
  },
  getters: {
    /**
     * Return all active AccessManagers
     *
     * @param {AccessManagerStoreState} state
     * @returns {AccessManager[]}
     */
    active: function active(state) {
      return state.managers;
    },
    /**
     * Return all discounted AccessManagers
     *
     * @param {AccessManagerStoreState} state
     * @returns {AccessManager[]}
     */
    discounted: function discounted(state) {
      return state.managers.filter(function (m) {
        return m.accessSubscription.price < m.accessTariff.price;
      });
    },
    /**
     * Return maximum number of inactive days
     *
     * This value should be the same on all managers, but just in case,
     * we show the maximum value (which means the least days left until
     * expiration).
     *
     * @param {AccessManagerStoreState} state
     * @returns {number}
     */
    inactiveDays: function inactiveDays(state) {
      return state.managers.reduce(function (value, manager) {
        return Math.max(value, manager.inactiveDays);
      }, 0);
    },
    /**
     * Return latest manager end date
     *
     * This value should be the same on all managers, but just in case,
     * we show the latest value (which means the latest date when
     * an access manager ends and restarts, resetting inactive days).
     *
     * @param {AccessManagerStoreState} state
     * @returns {Date}
     */
    endDate: function endDate(state) {
      return state.managers.reduce(function (value, manager) {
        if (!value) return new Date(manager.endDate);
        var endDate = new Date(manager.endDate);
        return endDate > value ? endDate : value;
      }, false);
    }
  },
  actions: {
    hydrate: function hydrate() {
      var _this = this;
      return _asyncToGenerator(/*#__PURE__*/regeneratorRuntime.mark(function _callee() {
        var data;
        return regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) switch (_context.prev = _context.next) {
            case 0:
              if (_this.$nuxt.$auth.user) {
                _context.next = 2;
                break;
              }
              return _context.abrupt("return", _this.hydrated = true);
            case 2:
              _context.next = 4;
              return _this.$nuxt.$axios.$get('/accessmanager/active').catch(function () {});
            case 4:
              data = _context.sent;
              if (data && data.managers) _this.managers = data.managers;
              if (data && data.config) _this.config = Object.assign(_this.config, data.config);
              _this.hydrated = true;
            case 8:
            case "end":
              return _context.stop();
          }
        }, _callee);
      }))();
    }
  }
});